import { doc, onSnapshot } from "firebase/firestore";
import { type UIEvent, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import PageTabs from "../../components/page-tabs/page-tabs";
import type { PageTabsProps } from "../../components/page-tabs/page-tabs.types";
import Toolbar from "../../components/toolbar/toolbar";
import { db } from "../../firebase/client";
import type { Broadcast } from "../../types/broadcasts.types";
import FeedHeader from "../broadcast-portal/modules/feed-module/feed-header/feed-header";
import FormationsModule from "../broadcast-portal/modules/formations-module/formations-module";
import ViewerCommentBar from "./components/comment-bar/viewer-comment-bar";
import ViewerComments from "./sub-modules/viewer-comments/viewer-comments";
import ViewerPolls from "./sub-modules/viewer-polls/viewer-polls";
import ViewerPostFeed from "./sub-modules/viewer-post-feed/viewer-post-feed";
import * as CSS from "./viewer-feed.styles";
import { ViewerJourneyProvider } from "./viewer-journey.context";

type BroadcastState =
	| { status: "loading" }
	| { status: "not-found" }
	| { status: "loaded"; broadcast: Broadcast };

const HEADER_MINIMISE_SCROLL_THRESHOLD = 24;

const ViewerFeed = () => {
	const { id } = useParams<{ id: string }>();
	const [activePage, setActivePage] = useState<string>("feed");
	const [broadcastState, setBroadcastState] = useState<BroadcastState>({
		status: "loading",
	});
	const [isContentScrolled, setIsContentScrolled] = useState(false);

	const handleContentScroll = (event: UIEvent<HTMLDivElement>) => {
		setIsContentScrolled(
			event.currentTarget.scrollTop > HEADER_MINIMISE_SCROLL_THRESHOLD,
		);
	};

	const isHeaderMinimised =
		isContentScrolled || (activePage !== "feed" && activePage !== "polls");

	useEffect(() => {
		if (!id) return;

		const unsubscribe = onSnapshot(doc(db, "broadcasts", id), (snap) => {
			if (!snap.exists()) {
				setBroadcastState({ status: "not-found" });
			} else {
				setBroadcastState({
					status: "loaded",
					broadcast: snap.data() as Broadcast,
				});
			}
		});

		return unsubscribe;
	}, [id]);

	const pages: PageTabsProps[] = useMemo(
		() => [
			{
				id: "feed",
				title: "Feed",
				children: (
					<ViewerPostFeed
						broadcastId={id ?? null}
						broadcast={
							broadcastState.status === "loaded"
								? broadcastState.broadcast
								: null
						}
					/>
				),
			},
			{
				id: "line-ups",
				title: "Line ups",
				children: (
					<FormationsModule
						fixtureId={
							broadcastState.status === "loaded"
								? broadcastState.broadcast.fixtureId
								: null
						}
						homeTeam={
							broadcastState.status === "loaded"
								? broadcastState.broadcast.homeTeam
								: undefined
						}
						awayTeam={
							broadcastState.status === "loaded"
								? broadcastState.broadcast.awayTeam
								: undefined
						}
						withPadding={false}
					/>
				),
			},
			{
				id: "comments",
				title: "Comments",
				children: (
					<ViewerComments
						broadcastId={id ?? null}
						authorId={
							broadcastState.status === "loaded"
								? (broadcastState.broadcast.userId ?? null)
								: null
						}
					/>
				),
			},
			{
				id: "polls",
				title: "Polls",
				children: <ViewerPolls broadcastId={id ?? null} />,
			},
		],
		[broadcastState, id],
	);

	const getActivePageChildren = useMemo(() => {
		return pages.find((page) => page.id === activePage)?.children ?? <div />;
	}, [activePage, pages]);

	return (
		<ViewerJourneyProvider>
			<CSS.ViewerFeed>
				<Toolbar />
				{broadcastState.status === "loaded" && (
					<FeedHeader
						{...broadcastState.broadcast}
						minimised={isHeaderMinimised}
					/>
				)}
				<PageTabs
					pages={pages}
					activeId={activePage ?? ""}
					onClick={(id) => setActivePage(id)}
				/>
				<CSS.ContentContainer onScroll={handleContentScroll}>
					{getActivePageChildren}
				</CSS.ContentContainer>
			</CSS.ViewerFeed>
			<ViewerCommentBar
				broadcastId={id ?? null}
				onCommentSent={() => setActivePage("comments")}
			/>
		</ViewerJourneyProvider>
	);
};

export default ViewerFeed;
